import React, { createContext, useContext, useRef, useState } from 'react';
import { noop, useQuery } from '@tanstack/react-query';
import {
  standardRequest,
  standardRequestWithErrorFlash,
} from 'shared/utils/request';
import { useUpdateEffect } from 'react-use';
import { useUserContext } from './user_context';

type RankingInProgressContextType = {
  rankingInProgress: boolean;
  hasCalibrationSetting: boolean;
  enablePolling: () => void;
};

const RankingInProgressContext = createContext<RankingInProgressContextType>({
  rankingInProgress: false,
  hasCalibrationSetting: false,
  enablePolling: noop,
});

export const RankingInProgressProvider = ({
  pollOnLoad,
  previouslyCalibrated,
  interval = 2000,
  children,
}: {
  pollOnLoad: boolean;
  previouslyCalibrated: boolean;
  interval?: number;
  children: React.ReactNode;
}) => {
  const { paths } = useUserContext();
  const [pollingEnabled, setPollingEnabled] = useState(pollOnLoad);
  const [hasCalibrationSetting, setHasCalibrationSetting] =
    useState(previouslyCalibrated);

  // This keeps the UI from showing the progress component until
  // after the first polling request. That way we can check if
  // ranking is in progress without flashing a loading state if
  // no ranking is happening.
  const [firstPoll, setFirstPoll] = useState(!pollOnLoad);
  const errorShown = useRef(false);

  useUpdateEffect(() => {
    if (pollingEnabled) {
      setFirstPoll(true);
      setHasCalibrationSetting(previouslyCalibrated);
      errorShown.current = false;
    }
  }, [pollingEnabled]);

  const query = useQuery({
    queryKey: ['rankingInProgress'],
    queryFn: async () => {
      const standardRequestWithOrWithoutError = errorShown.current
        ? standardRequest
        : standardRequestWithErrorFlash;

      try {
        const { data } = await standardRequestWithOrWithoutError.get(
          paths.rankingInProgressPath
        );

        errorShown.current = data.status === 'error';

        setFirstPoll(false);

        if (data.is_complete) {
          setPollingEnabled(false);
          setHasCalibrationSetting(data.has_calibration);
        }

        return data.is_complete;
      } catch (error) {
        errorShown.current = true;
        return false; // Keep polling after error
      }
    },
    staleTime: 0,
    enabled: pollingEnabled,
    refetchInterval: pollingEnabled ? interval : false,
  });

  const rankingInProgress =
    !firstPoll && (query.isLoading || query.data === false);

  return (
    <RankingInProgressContext.Provider
      value={{
        rankingInProgress,
        hasCalibrationSetting,
        enablePolling: () => setPollingEnabled(true),
      }}
    >
      {children}
    </RankingInProgressContext.Provider>
  );
};

export const useRankingInProgress = () => useContext(RankingInProgressContext);
